import { boneNames, partPaths, type ShapeEditorProps } from './doc-context.js';
import { arr, ListEditor, Num, num, obj, OptionalSection, patch, Pick, Section, str, vec, Vec, type Obj } from './fields.js';
import { RegionFields, regionSummary } from './region.js';

/** `garment`: clothing / armor fitted to another part's body surface. */
export function GarmentEditor({ shape, onChange, doc, partPath }: ShapeEditorProps) {
  const set = (key: string, v: unknown) => onChange(patch(shape, key, v));
  const bones = boneNames(doc);
  return (
    <>
      <Pick
        label="Target"
        hint="Body part (name or path) the garment is fitted to."
        none="Choose a body part…"
        options={partPaths(doc, partPath)}
        value={str(shape.target) || undefined}
        onChange={(v) => set('target', v ?? '')}
      />
      <Section title="Region" hint="Which part of the body it covers. Every set filter must match.">
        <RegionFields value={obj(shape.region) ?? {}} bones={bones} onChange={(v) => set('region', v)} />
      </Section>
      <Section title="Fit">
        <Num label="Offset" hint="Gap between body and garment (m)." min={0} step={0.001} value={num(shape.offset)} fallback={0.006} onChange={(v) => set('offset', v)} />
        <Num label="Looseness" hint="Extra room (m): 0 = skin-tight, 0.02 loose shirt, 0.05+ baggy." min={0} step={0.005} value={num(shape.looseness)} fallback={0} onChange={(v) => set('looseness', v)} />
        <Num
          label="Smooth"
          hint="Membrane passes: the garment is pulled taut over the body, bridging hollows (0 = follows every muscle, 4-10 fabric, 15-40 domed plate armor)."
          integer
          min={0}
          value={num(shape.smooth)}
          fallback={4}
          onChange={(v) => set('smooth', v)}
        />
        <Num
          label="Convex"
          hint="Rigid plate (0..1): how far the garment is stretched out to the convex hull of the covered body. 0 = cloth, 0.6-0.8 shaped plate, 1 = fully domed (breastplates, helmets)."
          min={0}
          max={1}
          step={0.05}
          value={num(shape.convex)}
          fallback={0}
          onChange={(v) => set('convex', v)}
        />
        <Num
          label="Soft edge"
          hint="Soft edge (m along the surface, 0 = clean cut): fades out through vertex alpha (makeup, tattoos, dirt, blush; use a material with alphaMode blend)."
          min={0}
          step={0.005}
          value={num(shape.softEdge)}
          fallback={0}
          onChange={(v) => set('softEdge', v)}
        />
        <Num label="Rim" hint="Rolled edge (m): a round bead along every edge, as on real plate armor (0.002-0.006); 0 = plain edge (cloth)." min={0} step={0.001} value={num(shape.rim)} fallback={0} onChange={(v) => set('rim', v)} />
        <Num
          label="Thickness"
          hint="Material thickness (m) with a rim: 0 = single sheet (cloth), 0.003-0.01 leather / plate, 0.03+ saddles and padding."
          min={0}
          step={0.001}
          value={num(shape.thickness)}
          fallback={0}
          onChange={(v) => set('thickness', v)}
        />
      </Section>
      <Section title="Openings & pieces" open={Boolean(arr(shape.cuts).length || shape.lames || shape.hem)}>
        <ListEditor
          title="Cuts"
          hint="Openings cut after fitting (visor slits, breaths, lacing holes): triangles inside any of these regions are removed."
          items={arr<unknown>(shape.cuts).map((c) => obj(c) ?? {})}
          onChange={(v) => set('cuts', v.length ? v : undefined)}
          make={() => ({ spheres: [{ center: [0, 0, 0], radius: 0.03 }] })}
          addLabel="Add cut"
          itemTitle={(c) => regionSummary(c)}
          render={(c, setC) => <RegionFields value={c} bones={bones} onChange={setC} />}
        />
        <OptionalSection
          title="Lames"
          hint="Articulated plates: the fitted piece is split into overlapping bands (finger joints, sabatons, faulds, couters, spaulders)."
          value={obj(shape.lames)}
          make={() => ({ count: 4, axis: [0, -1, 0] })}
          onChange={(v) => set('lames', v)}
        >
          {(l: Obj, setL) => (
            <>
              <Num label="Count" hint="Number of bands." integer min={1} value={num(l.count)} fallback={4} onChange={(v) => setL('count', v)} />
              <Vec label="Axis" hint="Direction the bands follow each other (e.g. wrist → fingertips)." value={vec(l.axis, 3) ?? [0, -1, 0]} onChange={(v) => setL('axis', v)} />
              <Num label="Step" hint="Each band sits this much further out than the previous one (m), so they overlap like real lames." min={0} step={0.001} value={num(l.step)} fallback={0.003} onChange={(v) => setL('step', v)} />
              <Num label="Overlap" hint="Each band is stretched this fraction past its neighbour." min={0} step={0.05} value={num(l.overlap)} fallback={0.15} onChange={(v) => setL('overlap', v)} />
            </>
          )}
        </OptionalSection>
        <OptionalSection
          title="Hem"
          hint="Free cloth hanging from the lower edge (skirts, coat tails, blankets)."
          value={obj(shape.hem)}
          make={() => ({ length: 0.3 })}
          onChange={(v) => set('hem', v)}
        >
          {(h: Obj, setH) => (
            <>
              <Num label="Length" hint="Length (m)." min={0} value={num(h.length)} fallback={0.3} onChange={(v) => setH('length', v)} />
              <Vec label="Direction" hint="Direction it hangs (default down)." value={vec(h.direction, 3) ?? [0, -1, 0]} onChange={(v) => setH('direction', v)} />
              <Num label="Flare" hint="Outward flare per meter of length (0 = straight, 0.5 A-line, 1.5 ball gown)." min={0} step={0.05} value={num(h.flare)} fallback={0.3} onChange={(v) => setH('flare', v)} />
              <Num label="Rows" hint="Rows of cloth along the length." integer min={1} value={num(h.rows)} fallback={10} onChange={(v) => setH('rows', v)} />
              <Num label="From" hint="Edges in the lowest `from` fraction of the garment's height grow the hem." min={0} max={1} step={0.05} value={num(h.from)} fallback={0.2} onChange={(v) => setH('from', v)} />
            </>
          )}
        </OptionalSection>
      </Section>
    </>
  );
}
